import React, { useCallback, useEffect, useMemo } from 'react';
import {
  TableInputRowData,
  TableInputColumn,
  ChangeEvent,
  TableInputDataByPage,
  TableInputOnChange,
  TableInputRecordId,
  TableInputRowValidator,
  TableId,
} from './tableInput.types';

import { mdiAlertCircle as errorIcon, mdiDelete as deleteIcon, mdiPlusBox as addIcon } from '@mdi/js';
import { Table } from '@trussworks/react-uswds';

import { ModalContent } from '@components/secondary-modal';
import Icon from '@components/icon/Icon';
import Loader from '@components/loader/Loader';
import Pagination from '@components/pagination/Pagination';
import Tooltip from '@components/tooltip/tooltip';
import EmptyTableRow from '@components/table/EmptyTableRow';
import TableHeaderCell from '@components/table/TableHeaderCell';
import SelectInput from '@components/select/SelectInput.base';
import TextInput from '@components/text-input/TextInput.base';

import { useModal, useTableInput } from '@stores';

import _debounce from 'lodash/debounce';
import _cloneDeep from 'lodash/cloneDeep';
import classNames from 'classnames';

import { QuestionValue } from '../dynamic-form/types';
import { QUESTION_TYPE } from '../dynamic-form/dynamic-form.validation';
import { SelectInputOption } from '@src/api/types';

const DEFAULT_ITEMS_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [3, 5, 10, 25, 50];

const ErrorTextModal = ({ errorText }: { errorText: string }) => (
  <ModalContent hasCloseButton={true} size='md' title='Table Input Validation Error'>
    <div className='modal-body'>{errorText}</div>
  </ModalContent>
);

type TableInputProps = {
  answerData: TableInputDataByPage;
  columnData: TableInputColumn[];
  getBlankRow: () => TableInputRowData;
  initialItemsPerPage?: number;
  invalidUnloadedPageNumbers?: number[];
  isDataLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isTooltipClickable?: boolean;
  labelText?: string;
  onBeforeSort?: () => Promise<boolean>; // Runs before sorting; return true to continue or false to cancel
  onBulkSelectionChange?: (action: 'add' | 'remove', selectedId: TableInputRecordId) => void;
  onChange: ({ action, isValid, value, colIndex, pageNumber }: TableInputOnChange) => void;
  onInvalidLoadedPageNumbersChange?: (pageNumbers: number[]) => void;
  onRemove?: (tableId: TableId) => void;
  onValidateRow: TableInputRowValidator;
  onValidationChange: (isValid: boolean) => void; // Notifies the parent whenever the overall table validation state changes.
  showPageSizeSelector?: boolean;
  startingTotalRowCount: number;
  tableId: TableId;
  tableName?: string;
  tooltipText?: string;
};

const TableInput: React.FC<TableInputProps> = ({
  answerData,
  columnData,
  getBlankRow,
  initialItemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  invalidUnloadedPageNumbers = [],
  isDataLoading,
  isReadOnly,
  isRequired,
  isTooltipClickable,
  labelText,
  onBeforeSort,
  onChange,
  onInvalidLoadedPageNumbersChange,
  onRemove,
  onValidateRow,
  onValidationChange,
  showPageSizeSelector = true,
  startingTotalRowCount,
  tableId,
  tooltipText,
}: TableInputProps) => {
  const labelStyles = classNames('usa-label', { isRequired });
  const containerStyles = classNames('table-input-container', {
    'table-input-loading': isDataLoading,
  });

  const { openSecondaryModal } = useModal();
  const {
    getTableInputBlankRow,
    getTableInputColumnData,
    getTableInputData,
    getTableInputDeleteExclusions,
    getTableInputDeletes,
    getTableInputItemsPerPage,
    getTableInputPageNumber,
    getTableInputRowCount,
    getTableInputSelectAllMode,
    getTableInputSortColumn,
    getTableInputSortDirection,
    getTableInputUpdates,

    setTableInputAnswer,
    setTableInputColumnData,
    setTableInputDataByPage,
    setTableInputItemsPerPage,
    setTableInputPageNumber,
    setTableInputRowCount,
    setTableInputSelectAllMode,
    setTableInputSortColumn,
    setTableInputSortDirection,
  } = useTableInput();

  const isInSelectAllMode = getTableInputSelectAllMode(tableId);
  const itemsPerPage = getTableInputItemsPerPage(tableId) || initialItemsPerPage;
  const pageNumber = getTableInputPageNumber(tableId);
  const sortColumn = getTableInputSortColumn(tableId);
  const sortDirection = getTableInputSortDirection(tableId);
  const tableColumnData = getTableInputColumnData(tableId);
  const tableDeleteExclusions = getTableInputDeleteExclusions(tableId);
  const tableDeletes = getTableInputDeletes(tableId);
  const tableInputData = getTableInputData(tableId);
  const tableUpdates = getTableInputUpdates(tableId);
  const totalRowCount = getTableInputRowCount(tableId);

  const pageCount = Math.ceil(totalRowCount / itemsPerPage);
  const pagedDataString = JSON.stringify(tableInputData?.pagedData);
  const isRemoving = isInSelectAllMode || Object.keys(tableDeletes ?? {}).length > 0;

  // a cell is valid when it satisfies required field validation and has no custom validation errors
  const { validatedDataByPage, invalidPageNumbers } = useMemo(() => {
    const invalidPageNumbers: { [key: number]: boolean } = {};
    const dataByPage = _cloneDeep(tableInputData?.pagedData ?? {});

    if (!onValidateRow) {
      return {
        invalidPageNumbers: Object.keys(invalidPageNumbers).map(Number),
        validatedDataByPage: dataByPage,
      };
    }

    const activeRows: TableInputRowData[] = Object.values(dataByPage)
      .flatMap(({ rowData: pageRowData }) => pageRowData)
      .filter((rowData) => {
        const recordId = rowData?.[0]?.recordId;
        const rowUpdates = tableUpdates?.[recordId];

        if (!!rowUpdates) {
          rowData.forEach((cellData, colIndex) => {
            const updatedCellValue = rowUpdates?.[colIndex];
            if (updatedCellValue !== undefined) {
              cellData.value = updatedCellValue;
            }
          });
        }

        const isActive = isInSelectAllMode ? tableDeleteExclusions?.[recordId] : !tableDeletes?.[recordId];

        return isActive;
      });

    for (let pageKey = 1; pageKey <= pageCount; pageKey++) {
      const pageNumber = Number(pageKey);
      const pageRowData = dataByPage[pageNumber]?.rowData ?? [];
      const isPageLoaded = pageRowData.length > 0;

      const isPageFormValid = pageRowData.reduce((acc, rowData) => {
        const recordId = rowData?.[0]?.recordId;
        const isRowDeleted = isInSelectAllMode ? !tableDeleteExclusions?.[recordId] : tableDeletes?.[recordId];
        const rowUpdates = tableUpdates?.[recordId];

        if (isRowDeleted) {
          delete invalidPageNumbers[pageNumber];
          return acc;
        }

        if (!!rowUpdates) {
          rowData.forEach((cellData, colIndex) => {
            const updatedCellValue = rowUpdates?.[colIndex];
            if (updatedCellValue !== undefined) {
              cellData.value = updatedCellValue;
            }
          });
        }

        const rowErrors = onValidateRow(rowData, activeRows);
        rowErrors.forEach(({ colIndex, message }) => {
          const cellData = rowData[colIndex];
          if (cellData) {
            cellData.errorText = message;
          }
        });

        return acc && !rowErrors.length;
      }, true);

      // if page is unloaded AND inSelectAllMode then all are deleted -> then page is valid, otherwise fallback on invalidUnloadedPageNumbers to determine validity
      const isUnloadedPageValid =
        !isPageLoaded && isInSelectAllMode ? true : !invalidUnloadedPageNumbers.includes(pageNumber);

      const isPageValid = isPageLoaded ? isPageFormValid : isUnloadedPageValid;

      if (isPageValid) {
        delete invalidPageNumbers[pageNumber];
      } else {
        invalidPageNumbers[pageNumber] = true;
      }
    }

    return {
      invalidPageNumbers: Object.keys(invalidPageNumbers).map(Number),
      validatedDataByPage: dataByPage,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    invalidUnloadedPageNumbers,
    isInSelectAllMode,
    onValidateRow,
    pageCount,
    pagedDataString,
    tableDeleteExclusions,
    tableDeletes,
    tableInputData?.pagedData,
    tableUpdates,
  ]);

  const validatedPageData = validatedDataByPage?.[pageNumber]?.rowData || [];

  const isTableValid = invalidPageNumbers.length === 0 && totalRowCount > 0;

  const handleAddRow = useCallback(() => {
    const goToPage = Math.ceil((totalRowCount + 1) / itemsPerPage);
    const newRow = getBlankRow ? getBlankRow() : getTableInputBlankRow(tableId);

    setTableInputAnswer(
      {
        action: 'add',
        newRow,
      },
      tableId
    );
    setTableInputPageNumber(tableId, goToPage);

    onChange({
      action: 'add',
      isValid: isTableValid,
      colIndex: 0,
      pageNumber: goToPage,
    });
  }, [
    getBlankRow,
    getTableInputBlankRow,
    isTableValid,
    itemsPerPage,
    onChange,
    tableId,
    setTableInputAnswer,
    setTableInputPageNumber,
    totalRowCount,
  ]);

  const handleOnChange = useMemo(
    () =>
      _debounce(
        ({
          onChangeProps,
          value,
        }: {
          onChangeProps: {
            colIndex: number;
            recordId: TableInputRecordId;
            rowIndex: number;
          };
          value: QuestionValue;
        }) => {
          const { colIndex, recordId, rowIndex } = onChangeProps;

          onChange({
            action: 'update',
            colIndex,
            rowIndex,
            isValid: isTableValid,
            pageNumber,
            recordId,
            value,
          });
        },
        500
      ),
    [onChange, pageNumber, isTableValid]
  );

  const handleInputChange = useCallback(
    ({ value: inputValue, onChangeProps }: { value: string | number; onChangeProps?: Record<string, unknown> }) => {
      if (!onChangeProps) {
        return;
      }
      const { colIndex, recordId, rowIndex } = onChangeProps as {
        colIndex: number;
        recordId: TableInputRecordId;
        rowIndex: number;
      };
      const hasTargetValue = (input: unknown): input is ChangeEvent =>
        typeof input === 'object' && input !== null && 'target' in input;
      const value: QuestionValue = hasTargetValue(inputValue) ? inputValue.target?.value : inputValue; // Update table state immediately so validation sees the new value.
      setTableInputAnswer(
        {
          action: 'update',
          colIndex,
          recordId,
          value,
        },
        tableId
      );
      handleOnChange({
        onChangeProps: {
          colIndex,
          recordId,
          rowIndex,
        },
        value,
      });
    },
    [handleOnChange, setTableInputAnswer, tableId]
  );

  const handleDeleteRow = useCallback(
    (recordId: TableInputRecordId, rowIndex: number) => {
      setTableInputAnswer(
        {
          action: 'delete',
          recordId,
          rowIndex,
        },
        tableId
      );
      onChange({
        action: 'delete',
        isValid: isTableValid,
        pageNumber,
        recordId,
      });
    },
    [isTableValid, onChange, pageNumber, tableId, setTableInputAnswer]
  );

  const handleUndoDeleteRow = useCallback(
    (recordId: TableInputRecordId) => {
      setTableInputAnswer(
        {
          action: 'undo-delete',
          recordId,
        },
        tableId
      );

      onChange({
        action: 'undo-delete',
        isValid: isTableValid,
        colIndex: 0,
        pageNumber,
        recordId,
      });
    },
    [isTableValid, onChange, pageNumber, tableId, setTableInputAnswer]
  );

  const openErrorTextModal = useCallback(
    (errorText: string) => {
      openSecondaryModal(ErrorTextModal, { errorText });
    },
    [openSecondaryModal]
  );

  const handleSortClick = useCallback(
    async (name: string, direction: 'asc' | 'desc') => {
      const isNewSortColumn = name !== sortColumn;
      const isNewSortDirection = direction !== sortDirection;

      if (!isNewSortColumn && !isNewSortDirection) {
        return;
      }

      if (onBeforeSort) {
        handleOnChange.flush();
        const canSort = await onBeforeSort();
        if (!canSort) {
          return;
        }
      }
      if (isNewSortColumn) {
        setTableInputSortColumn(tableId, name);
      }
      if (isNewSortDirection) {
        setTableInputSortDirection(tableId, direction);
      }
    },
    [
      handleOnChange,
      onBeforeSort,
      tableId,
      setTableInputSortColumn,
      setTableInputSortDirection,
      sortColumn,
      sortDirection,
    ]
  );

  const handleSelectAll = useCallback(
    (areAllSelected: boolean) => {
      setTableInputSelectAllMode(tableId, !areAllSelected);
    },
    [setTableInputSelectAllMode, tableId]
  );

  const tableHeaders = useMemo(
    () => (
      <thead>
        <tr>
          <th className='actions-header'>
            {!isReadOnly && totalRowCount > 0 && (
              <input type='checkbox' onClick={() => handleSelectAll(isInSelectAllMode)} />
            )}
          </th>
          {tableColumnData?.map((column: TableInputColumn) => (
            <TableHeaderCell
              className={column.isRequired ? 'required' : undefined}
              helpText={column.helpText}
              key={`${column.columnId}`}
              label={column.columnLabel}
              name={column.columnLabel}
              onClick={handleSortClick}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              width={`${column.width}px`}
            />
          ))}
        </tr>
      </thead>
    ),
    [
      handleSelectAll,
      handleSortClick,
      isInSelectAllMode,
      isReadOnly,
      tableColumnData,
      totalRowCount,
      sortColumn,
      sortDirection,
    ]
  );

  // Set initial table data on load
  useEffect(() => {
    setTableInputColumnData(tableId, columnData);
  }, [columnData, tableId, setTableInputColumnData]);

  // Load page data into the table store.
  useEffect(() => {
    // update the row count before setting the data - need the updated value in the update
    setTableInputRowCount(tableId, startingTotalRowCount);
    if (Object.keys?.(answerData)?.length > 0) {
      setTableInputDataByPage(tableId, answerData);
    }
  }, [answerData, tableId, setTableInputRowCount, setTableInputDataByPage, startingTotalRowCount]);

  useEffect(() => {
    setTableInputItemsPerPage(tableId, initialItemsPerPage);
  }, [initialItemsPerPage, tableId, setTableInputItemsPerPage]);

  // Notify the parent whenever the table's overall validation state changes.
  useEffect(() => {
    onValidationChange?.(isTableValid);
  }, [isTableValid, onValidationChange]);

  // Notify the parent which currently loaded pages contain local validation errors.
  useEffect(() => {
    onInvalidLoadedPageNumbersChange?.(invalidPageNumbers);
  }, [invalidPageNumbers, onInvalidLoadedPageNumbersChange]);

  // Cancel pending debounced changes when this component unmounts.
  useEffect(
    () => () => {
      handleOnChange.cancel();
    },
    [handleOnChange]
  );

  return (
    <fieldset className={containerStyles} id={`${tableId}`}>
      <legend className={labelStyles}>
        <span id={`${tableId}_label`}>{labelText}</span>
        {tooltipText && <Tooltip clickable={isTooltipClickable} content={tooltipText} header={labelText} />}
      </legend>
      {invalidPageNumbers.length > 0 && (
        <div className='table-input-error-message'>Incomplete pages: {invalidPageNumbers.join(', ')}</div>
      )}
      <div className='table-input-top-content'>
        <div className='table-input-row-count'>{totalRowCount} Rows</div>

        <div className='table-input-bulk-edit-buttons'>
          {isRemoving && (
            <button
              className='table-input-button primary warning table-input-delete-btn'
              type='button'
              onClick={() => onRemove?.(tableId)}
            >
              <Icon path={deleteIcon} size={0.9} />
              Remove Selected
            </button>
          )}
        </div>
      </div>

      {isDataLoading && (
        <>
          <Table bordered>{tableHeaders}</Table>
          <div className='table-input-loading-container'>
            <Loader variant='section' />
          </div>
        </>
      )}
      {!isDataLoading && (
        <>
          <div className='table-scroll-container'>
            <Table bordered>
              {tableHeaders}
              <tbody>
                {totalRowCount === 0 && <EmptyTableRow isReadOnly={isReadOnly} placeholderText='No records found.' />}
                {validatedPageData?.map((rowData: TableInputRowData, rowIndex: number) => {
                  const recordId = rowData[0].recordId;
                  const isRowDeleted = isInSelectAllMode
                    ? !tableDeleteExclusions?.[recordId]
                    : tableDeletes?.[recordId];
                  const rowKey = `recordId:${recordId}-page:${pageNumber}-row:${rowIndex}-sort:${sortColumn}-dir:${sortDirection}`;
                  const rowStyles = classNames({ 'table-row-deleted': isRowDeleted });

                  return (
                    <tr key={rowKey} className={rowStyles}>
                      <td className='actions-cell'>
                        <input
                          type='checkbox'
                          checked={isRowDeleted}
                          disabled={isReadOnly}
                          onClick={() =>
                            isRowDeleted ? handleUndoDeleteRow(recordId) : handleDeleteRow(recordId, rowIndex)
                          }
                        />
                      </td>
                      {tableColumnData?.map((columnData, colIndex: number) => {
                        const cellData = rowData[colIndex];
                        const updatedValue = tableUpdates?.[recordId]?.[colIndex] ?? cellData?.value;
                        const cellKey = `${rowKey}-recordId:${recordId}-colIndex:${colIndex}`;
                        const options = cellData?.options || columnData.options;
                        const normalizedInputValue =
                          typeof updatedValue === 'string' || typeof updatedValue === 'number' ? updatedValue : '';
                        const commonInputProps = {
                          defaultValue: normalizedInputValue,
                          hasError: !!cellData?.errorText,
                          isReadOnly,
                          isRequired: columnData.isRequired,
                          isValid: isRequired && !updatedValue,
                          label: columnData.columnLabel,
                          name: cellKey,
                          onChange: handleInputChange,
                          onChangeProps: {
                            rowIndex,
                            colIndex,
                            recordId,
                          },
                        };

                        return (
                          <td key={cellKey}>
                            {cellData?.errorText && (
                              <Icon
                                className='table-input-error-icon'
                                onClick={() =>
                                  openErrorTextModal(
                                    cellData.errorText ?? columnData.errorText ?? 'This field is invalid'
                                  )
                                }
                                path={errorIcon}
                                size={0.8}
                              />
                            )}
                            {columnData.questionTypeId === QUESTION_TYPE.shortAnswer && (
                              <TextInput type='text' {...commonInputProps} />
                            )}
                            {columnData.questionTypeId === QUESTION_TYPE.number && (
                              <TextInput {...commonInputProps} type='number' />
                            )}
                            {columnData.questionTypeId === QUESTION_TYPE.option && (
                              <SelectInput {...commonInputProps} options={options as SelectInputOption[]} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <Pagination
            currentPage={pageNumber}
            itemsPerPage={itemsPerPage}
            onPageChange={(number) => setTableInputPageNumber(tableId, number)}
            onPageSizeChange={(number) => setTableInputItemsPerPage(tableId, number)}
            pageCount={pageCount}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            rightContent={
              <button
                className='table-input-button table-input-add-btn'
                disabled={isReadOnly || isInSelectAllMode}
                onClick={handleAddRow}
                type='button'
              >
                <Icon path={addIcon} size={0.9} /> Add Row
              </button>
            }
            showPageSizeSelector={showPageSizeSelector}
          />
        </>
      )}
    </fieldset>
  );
};

export default TableInput;
